import {
  Material,
  MaterialGroup,
  sandwichHasHPL,
  SheetSizeSelectionOption,
} from '@cutr/constants/cutlist';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useActiveGroupAiSuggestions } from '@/api/aiSuggestions';
import {
  getCoreMaterials,
  getCoreMaterialsForGluingAndPressing,
  getHPLMaterials,
  groupSearchResultsByVariationGroupAndThickness,
} from '@/api/materials';
import { MaterialSearchType } from '@/api/materialSearch';
import { MaterialComponent, useMatchingMaterials } from '@/api/materialsGroup';
import { selectHasMaterials, useCutlistState } from '@/api/store';
import Card from '@/blocks/Card';
import { InfoMessage } from '@/blocks/InfoMessage';
import { MaterialRow } from '@/blocks/MaterialRow';
import {
  MaterialSelectedProps,
  MaterialVariationRow,
} from '@/blocks/MaterialVariationRow';
import { Modal } from '@/blocks/Modal';
import TabNav from '@/blocks/TabNav';
import { Icon, Star } from '@/primitives/Icons';
import { useCurrentFeatures, useTicker } from '@/theme';

import styles from '../PartTableCommon/styles.module.css';
import { Autocomplete } from './Autocomplete';
import moduleStyles from './SelectMaterialModal.module.css';
import { MaterialUpdate } from './types';

const ModalPrompt = ({ children }: { children: string | React.ReactNode }) => {
  return (
    <div className={styles.modalPrompt}>
      <div className={styles.content}>{children}</div>

      <div className={styles.background}></div>
    </div>
  );
};

const AutomaticSheetSizeSearchResults = (props: {
  group: MaterialGroup;
  variationGroupResults: Material[][];
  onMaterialSelected: (props: MaterialSelectedProps) => void;
}) => {
  return (
    <>
      {props.variationGroupResults.map((materials) => (
        <MaterialVariationRow
          role="option"
          tabIndex={-1}
          aria-selected="false"
          className="materialRow"
          items={materials}
          group={props.group}
          key={materials[0].id}
          onMaterialSelected={props.onMaterialSelected}
        />
      ))}
    </>
  );
};

const ManualSheetSizeSearchResults = (props: {
  materialResults: Material[];
  onMaterialSelected: (props: MaterialSelectedProps) => void;
}) => {
  return (
    <>
      {props.materialResults.map((m) => (
        <MaterialRow
          role="option"
          tabIndex={-1}
          aria-selected="false"
          className="materialRow"
          item={m}
          key={m.id}
          onSelected={(selectedMaterial) =>
            props.onMaterialSelected({
              selectedMaterial,
              automaticSheetSizeMaterials: [],
              sheetSizeSelection: 'manual',
            })
          }
        />
      ))}
    </>
  );
};

interface SearchTabProps {
  id: string;
  field: MaterialComponent;
  item: Material['articleCode'];
  group: MaterialGroup;
  materialResults: Material[];
  materialSearchType: MaterialSearchType;
  onMaterialResults: (results: Material[]) => void;
  onMaterialSelected: (props: MaterialSelectedProps) => void;
}

const getMaterialCatalog = (materialSearchType: MaterialSearchType) => {
  if (materialSearchType === 'coreForGluingAndPressing')
    return getCoreMaterialsForGluingAndPressing();
  if (materialSearchType === 'hpl') return getHPLMaterials();
  return getCoreMaterials();
};

const SearchTab = ({
  id,
  field,
  item,
  group,
  materialResults,
  materialSearchType,
  onMaterialSelected,
  onMaterialResults,
}: SearchTabProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState(0); // the index of the tab
  const [isDefaultList, setIsDefaultList] = React.useState(true);
  const { sheetSizeSelection } = useCurrentFeatures();
  const groupLlmSuggestions = useActiveGroupAiSuggestions();

  const shouldShowMatchingMaterials = Boolean(
    materialResults.length <= 3 && materialSearchType === 'edgeband'
  );
  const matchingMaterials = useMatchingMaterials();

  const getSheetSizeSelectionTabKeys = (): SheetSizeSelectionOption[] => {
    if (field === 'edgeband') {
      return ['manual'];
    }

    if (sheetSizeSelection === 'automatic-and-manual') {
      return ['automatic', 'manual'];
    }

    if (sheetSizeSelection === 'automatic') {
      return ['automatic'];
    }

    return ['manual'];
  };
  const sheetSizeSelectionTabKeys = getSheetSizeSelectionTabKeys();

  const sheetSizeSelectionTabs: string[] = sheetSizeSelectionTabKeys.map(
    (key) => t(`cutlist-form.selectMaterialModal.sheetSizeSelection.${key}`)
  );

  const hasMaterials = useCutlistState(selectHasMaterials);
  const hasAutomaticTab = sheetSizeSelectionTabKeys.includes('automatic');

  const variationGroupResults = React.useMemo(() => {
    if (!hasAutomaticTab) return [];
    const source = isDefaultList
      ? getMaterialCatalog(materialSearchType)
      : materialResults;
    return groupSearchResultsByVariationGroupAndThickness(source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAutomaticTab,
    isDefaultList,
    materialResults,
    materialSearchType,
    hasMaterials,
  ]);

  const resultCount =
    sheetSizeSelectionTabKeys[activeTab] === 'automatic'
      ? variationGroupResults.length
      : materialResults.length;

  return (
    <Card>
      <div className="stack">
        {groupLlmSuggestions[field] && (
          <InfoMessage
            message={t('agent.quoteFlow.llmFlow.aiSuggestions', {
              suggestions: groupLlmSuggestions[field],
            })}
            variant="ai"
          />
        )}
        <Autocomplete
          placeholder={t('cutlist-form.selectMaterialModal.placeholder')}
          id={id}
          name={field}
          materialSearchType={materialSearchType}
          value={item || groupLlmSuggestions[field] || ''}
          hasSelection={Boolean(item)}
          aria-label={t(`cutlist-form.field.${field}.label`)}
          onResults={(results, isDefault) => {
            setIsDefaultList(isDefault);
            onMaterialResults(results);
          }}
        />

        {sheetSizeSelectionTabs.length > 1 && (
          <TabNav
            className={moduleStyles.sheetSizeSelectionTabNav}
            tabs={sheetSizeSelectionTabs}
            activeTab={activeTab}
            onChangeTab={setActiveTab}
          />
        )}
        {materialResults.length ? (
          <p className={cn([styles.resultsCount, 'flexAlign', 'alignEnd'])}>
            {t(`cutlist-form.materialCount`, { count: resultCount })}
          </p>
        ) : null}
        <ModalPrompt>
          {(materialResults.length || matchingMaterials.length) > 0 ? (
            <div className={styles.materialList}>
              {shouldShowMatchingMaterials && (
                <MatchingEdgebandList
                  matchingMaterials={matchingMaterials}
                  onMaterialSelected={(selectedMaterial) =>
                    onMaterialSelected({
                      selectedMaterial,
                      automaticSheetSizeMaterials: [],
                      sheetSizeSelection: 'manual',
                    })
                  }
                />
              )}
              {sheetSizeSelectionTabKeys[activeTab] === 'automatic' ? (
                <AutomaticSheetSizeSearchResults
                  group={group}
                  variationGroupResults={variationGroupResults}
                  onMaterialSelected={onMaterialSelected}
                />
              ) : (
                <ManualSheetSizeSearchResults
                  materialResults={materialResults}
                  onMaterialSelected={onMaterialSelected}
                />
              )}
            </div>
          ) : (
            <p>{t('cutlist-form.selectMaterialModal.start')}</p>
          )}
        </ModalPrompt>
      </div>
    </Card>
  );
};

const MatchingEdgebandList = (props: {
  matchingMaterials: Material[];
  onMaterialSelected: (selected: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {Boolean(props.matchingMaterials.length) && (
        <>
          <h4
            className={styles.dropdownTitle}
            style={{ color: 'var(--calendulaGold)' }}
          >
            <Icon icon={<Star />} size={2} />
            <span>{t('cutlist-form.matchingEdgeband.title')}</span>
          </h4>

          {props.matchingMaterials.map((m) => (
            <MaterialRow
              role="option"
              tabIndex={-1}
              aria-selected="false"
              className="materialRow"
              item={m}
              key={m.id}
              onSelected={props.onMaterialSelected}
            />
          ))}
        </>
      )}
    </>
  );
};

interface SelectMaterialModalProps {
  prompt?: string | React.ReactNode;
  field: MaterialComponent;
  group: MaterialGroup;
  isOpen: boolean;
  handleClose: () => void;
  onUpdateMaterial: MaterialUpdate;
}

const SelectMaterialModal = ({
  field,
  group,
  isOpen,
  handleClose,
  onUpdateMaterial,
}: SelectMaterialModalProps) => {
  const { t } = useTranslation();
  const id = `${field}_${group.id}`;
  const itemArticleCode = group[field] as Material['articleCode'];
  const [materialResults, setMaterialResults] = React.useState<Material[]>([]);
  const source = useTicker();

  const getMaterialSearchType = (field: MaterialComponent) => {
    if (field === 'core1' && sandwichHasHPL(group.materialSandwichType))
      return 'coreForGluingAndPressing';
    if (field === 'core1') return 'core';
    if (field === 'edgeband') return 'edgeband';
    return 'hpl';
  };

  const materialSearchType = getMaterialSearchType(field);

  const onMaterialSelected = (props: MaterialSelectedProps) => {
    const inputElement = document.getElementById(
      'autocomplete-input-' + id
    ) as HTMLInputElement;
    const searchInput = inputElement?.value;
    const material = materialResults.find(
      (m) => m.articleCode === props.selectedMaterial
    );
    window.analytics.track('Selected material', {
      field,
      materialSearchType: materialSearchType,
      articleCode: props.selectedMaterial,
      supplierCode: material?.supplierCode,
      name: material?.name,
      searchInput,
      success: true,
      ticker: source,
      resultsCount: materialResults.length,
    });
    onUpdateMaterial({
      [field]: props.selectedMaterial,
      sheetSizeSelection: props.sheetSizeSelection,
      automaticSheetSizeMaterials: props.automaticSheetSizeMaterials,
    });
  };

  return (
    <Modal
      title={t('cutlist-form.selectMaterialModal.title')}
      isOpen={isOpen}
      classes="stack"
      handleClose={() => {
        setMaterialResults([]);
        handleClose();
      }}
    >
      <SearchTab
        id={id}
        field={field}
        group={group}
        item={itemArticleCode}
        materialResults={materialResults}
        materialSearchType={materialSearchType}
        onMaterialSelected={onMaterialSelected}
        onMaterialResults={(results) => setMaterialResults(results)}
      />
    </Modal>
  );
};

export default SelectMaterialModal;
